import { ApolloError, useMutation } from '@apollo/client';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnClickOutside } from 'usehooks-ts';

import {
  DeleteAdMutation,
  DeleteAdMutationVariables
} from '@/__generated_graphql__/graphql';
import { CardMenuOptions, CardMenuProps } from '@/components/Card/Card.types';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { ThreeDotsIcon } from '@/components/ui/Icons/Icons';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { CardStatus } from '@/constants/common';
import { DELETE_AD } from '@/graphql/mutations/ad.mutations';
import { GET_PROFILE_FULL } from '@/graphql/queries';
import { useGraphQLErrorProcessor } from '@/hooks/useGraphQLErrorProcessor';
import { useLoader } from '@/providers/LoaderProvider';

import * as S from './Card.styles';

const { APPROVED, EXPIRED, REJECTED } = CardStatus;

export const CardMenu: FC<CardMenuProps> = ({
  id,
  subtitle,
  status,
  activeTab,
  adType
}) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState<{
    title: string;
    subtitle: string;
    confirmLabel: string;
    confirmDataCy?: string;
    onConfirm: () => Promise<void>;
  } | null>(null);

  const [deleteMutation] = useMutation<
    DeleteAdMutation,
    DeleteAdMutationVariables
  >(DELETE_AD, {
    refetchQueries: [
      { query: GET_PROFILE_FULL, variables: { state: activeTab } }
    ],
    awaitRefetchQueries: true
  });
  const menuRef = useRef<HTMLDivElement>(null);

  const handleToggleMenu = () => setIsOpen(prev => !prev);

  const handleGraphQLErrors = useGraphQLErrorProcessor();
  const { showLoader, hideLoader } = useLoader();

  const handleCancel = () => {
    setModalData(null);
    handleToggleMenu();
  };

  const handleDelete = async () => {
    try {
      showLoader(status === EXPIRED ? 'Card.deleting' : undefined);
      if (id) {
        await deleteMutation({
          variables: {
            id,
            adType
          }
        });
      }
      setModalData(null);
      handleToggleMenu();
      hideLoader();
    } catch (error) {
      handleGraphQLErrors({ error: error as ApolloError });
    }
  };

  const handleOpenDeleteModal = () => {
    setModalData({
      title: t('Card.delete_modal_title', { name: subtitle }),
      subtitle: t('Card.delete_modal_subtitle'),
      confirmLabel: t('Card.delete_button_label'),
      confirmDataCy: 'delete',
      onConfirm: handleDelete
    });
  };

  const handleOpenExpireModal = () => {
    setModalData({
      title: t('Card.expire_modal_title', { name: subtitle }),
      subtitle: t('Card.expire_modal_subtitle'),
      confirmLabel: t('Card.expire_button_label'),
      confirmDataCy: 'expire',
      onConfirm: handleDelete
    });
  };

  const menuOptions = {
    [APPROVED]: [
      {
        id: 'expire-button',
        label: 'Card.expire_label',
        onClick: handleOpenExpireModal
      }
    ],
    [EXPIRED]: [
      {
        id: 'delete-button',
        label: 'Card.delete_label',
        onClick: handleOpenDeleteModal
      }
    ],
    [REJECTED]: [
      {
        id: 'delete-button',
        label: 'Card.delete_label',
        onClick: handleOpenDeleteModal
      }
    ]
  } as CardMenuOptions;

  useOnClickOutside(menuRef, () => setIsOpen(false));

  if (!menuOptions[status]) {
    return null;
  }

  return (
    <S.MenuWrapper
      ref={menuRef}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <S.MenuButton
        data-cy="profile-card-menu-button"
        $isOpen={isOpen}
        onClick={handleToggleMenu}
      >
        <ThreeDotsIcon />
      </S.MenuButton>
      {isOpen && (
        <S.DropdownMenu>
          {menuOptions[status].map(option => (
            <S.DropdownItem
              data-cy={`profile-card-menu-${option.id}`}
              key={option.id}
              onClick={option.onClick}
            >
              <Typography variant="body2">{t(option.label)}</Typography>
            </S.DropdownItem>
          ))}
        </S.DropdownMenu>
      )}
      {modalData && (
        <ConfirmModal
          title={modalData.title}
          subtitle={modalData.subtitle}
          confirmLabel={modalData.confirmLabel}
          confirmDataCy={modalData.confirmDataCy}
          cancelLabel={t('Card.cancel_button_label')}
          onConfirm={modalData.onConfirm}
          onCancel={handleCancel}
        />
      )}
    </S.MenuWrapper>
  );
};
